<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div v-if="modelValue" class="fixed inset-0 z-50 flex flex-col justify-end">
        <div class="absolute inset-0 bg-black/60" @click="$emit('update:modelValue', false)"/>

        <div class="relative bg-gray-900 rounded-t-2xl p-5 pb-8 flex flex-col gap-3 max-h-80vh">
          <div class="w-10 h-1 bg-gray-700 rounded-full mx-auto mb-1"/>

          <div class="flex items-center justify-between">
            <p class="text-sm font-semibold text-gray-100">اعلان‌ها</p>
            <button v-if="hasUnreadNotifications" class="text-xs text-secondary border-0 bg-transparent p-0" :disabled="markingAll" @click="readAll">
              {{ markingAll ? 'در حال انجام...' : 'همه را خواندم' }}
            </button>
          </div>

          <div class="flex flex-col gap-3 overflow-y-auto">
            <p v-if="loading" class="text-xs text-gray-500 text-center py-6">در حال بارگذاری...</p>
            <p v-else-if="!notifications.length" class="text-xs text-gray-500 text-center py-6">
              اعلانی وجود ندارد
            </p>

            <div
              v-for="n in notifications"
              :key="n.id"
              class="flex flex-col gap-2 p-3 rounded-xl cursor-pointer"
              :class="n.isRead ? 'bg-gray-800' : 'bg-gray-800 border border-secondary/30'"
              @click="openNotification(n)"
            >
              <!-- Notification header -->
              <div class="flex items-center gap-2">
                <div class="relative flex flexCenter shrink-0">
                  <TheNuxtIcon name="notification" icon-type="svg" class="text-2xl text-gray-100"/>
                  <span v-if="!n.isRead" class="absolute top-0 right-0 w-2 h-2 rounded-full bg-secondary"/>
                </div>
                <div class="flex-1 text-right">
                  <p class="text-xs text-gray-100 font-medium">{{ typeLabel(n.type) }}</p>
                  <p v-if="typeHint(n.type)" class="text-2.5 text-secondary">{{ typeHint(n.type) }}</p>
                  <p class="text-2.5 text-gray-500">
                    {{ n.participant?.user?.profile?.firstName || '' }}
                    {{ n.participant?.user?.profile?.lastName || '' }}
                  </p>
                </div>
                <p class="text-2 text-gray-600 flex-shrink-0">{{ isoToDate(n.createdAt) }}</p>
                <div v-if="!n.isRead && (n.type === 'MATCH_INVITE' || n.type === 'JOIN_REQUEST')" class="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    class="px-3 py-1 text-xs bg-secondary border border-solid border-secondary text-white rounded-lg font-medium outline-none ring-0"
                    :disabled="acting === n.id"
                    @click.stop="accept(n)"
                  >
                    {{ acting === n.id ? '...' : 'قبول' }}
                  </button>
                  <button
                    class="px-3 py-1 text-xs bg-transparent border border-solid border-gray-600 text-gray-300 rounded-lg font-medium outline-none ring-0"
                    :disabled="acting === n.id"
                    @click.stop="reject(n)"
                  >
                    رد
                  </button>
                </div>
                <TheButton
                  v-else-if="!n.isRead"
                  mini
                  :fill="false"
                  color="#ec561e"
                  bg-color="transparent"
                  no-border
                  :label="markingNotificationId === n.id ? '...' : 'خواندم'"
                  :disable="markingNotificationId === n.id"
                  @click.stop="markRead(n)"
                />
              </div>

            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  refresh: []
}>()

const notifications = ref<any[]>([])
const loading = ref(false)
const acting = ref<string | null>(null)
const markingNotificationId = ref<string | null>(null)
const markingAll = ref(false)
const { $toast } = useNuxtApp()

const typeLabel = gameNotificationLabel
const typeHint = gameNotificationHint

const hasUnreadNotifications = computed(() => notifications.value.some((notification) => !notification.isRead))

const notificationTarget = (notification: any): string | null => {
  if (notification.matchId) return `/matches/${notification.matchId}`
  if (notification.tournamentId) return `/tournaments/${notification.tournamentId}`
  if (notification.leagueId) return `/leagues/${notification.leagueId}`
  if (notification.reservationId) return `/club/reserved/${notification.reservationId}`
  return null
}

const load = async () => {
  loading.value = true
  const res = await getGameNotifications()
  notifications.value = Array.isArray(res) ? res : res?.data || []
  loading.value = false
}

const markRead = async (n: any) => {
  if (n.isRead || markingNotificationId.value) return
  markingNotificationId.value = n.id
  const { error } = await markNotifRead(n.id)
  if (!error) {
    n.isRead = true
    emit('refresh')
  }
  markingNotificationId.value = null
}

const openNotification = async (n: any) => {
  if (!n.isRead) await markRead(n)
  const target = notificationTarget(n)
  if (target) {
    emit('update:modelValue', false)
    await navigateTo(target)
  }
}

const readAll = async () => {
  if (markingAll.value) return
  markingAll.value = true
  const { error } = await markAllNotifsRead()
  if (!error) {
    notifications.value.forEach((n) => (n.isRead = true))
    emit('refresh')
  }
  markingAll.value = false
}

const accept = async (n: any) => {
  if (!n.participantId || !n.matchId) return
  acting.value = n.id
  const { error } = await acceptParticipant(n.matchId, n.participantId)
  acting.value = null
  if (error) {
    $toast?.error('خطا در قبول درخواست')
  } else {
    await markRead(n)
    $toast?.success('قبول شد')
    load()
  }
}

const reject = async (n: any) => {
  if (!n.participantId || !n.matchId) return
  acting.value = n.id
  const { error } = await rejectParticipant(n.matchId, n.participantId)
  acting.value = null
  if (error) {
    $toast?.error('خطا در رد درخواست')
  } else {
    await markRead(n)
    $toast?.success('رد شد')
    load()
  }
}

watch(() => props.modelValue, (v) => {
  if (v) load()
})
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.25s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
